import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { catchError, EMPTY, filter, map } from 'rxjs';
import {
	GET_CLEARING_COURSE,
	GET_CLEARING_UNIVERSITY,
	GET_COURSE,
	GET_UNIVERSITY,
} from './queries.graphql';
import {
	ClearingCourseResponse,
	CourseResponse,
	UniversityClearingCourseResponse,
	UniversityCourseResponse,
} from '../models/course.models';
import { GraphQLErrorCode, ProductEnum } from '@uc/web/shared/data-models';
import { Router } from '@angular/router';
import { CourseService } from '../services/course.service';

@Injectable({
	providedIn: 'root',
})
export class CourseApiService {
	constructor(
		private _apollo: Apollo,
		private _router: Router,
		private _courseSrv: CourseService,
	) {}

	getCourse(courseSlug: string) {
		return this._apollo
			.query<CourseResponse>({
				query: GET_COURSE,
				variables: {
					courseSlug: courseSlug,
				},
			})
			.pipe(
				catchError(() => {
					return EMPTY;
				}),
				filter(({ data }) => !this._courseSrv.isInvalidCourse(data.errorCode)),
				map(({ data }) => data.course),
			);
	}

	getUniversity(uniSlug: string, product: string | undefined) {
		return this._apollo
			.query<UniversityCourseResponse>({
				query: GET_UNIVERSITY,
				variables: {
					uniSlug: uniSlug,
				},
			})
			.pipe(
				catchError((error) => {
					if (error.networkError) {
						const code = error.networkError.error?.data?.code;
						if (code === GraphQLErrorCode.UNI_404) {
							this._router.navigate([
								`/courses/${product ?? ProductEnum.Undergraduate}`,
							]);
						}
					}
					return EMPTY;
				}),
				map(({ data }) => ({
					...data.university,
					universityGallery: data.universityGallery,
				})),
			);
	}

	getClearingCourse(courseSlug: string) {
		return this._apollo
			.query<ClearingCourseResponse>({
				query: GET_CLEARING_COURSE,
				variables: {
					courseSlug: courseSlug,
				},
			})
			.pipe(
				catchError((error) => {
					const statusCode = error?.networkError.status;
					this._courseSrv.handleCourseError(statusCode);
					return EMPTY;
				}),
				map(({ data }) => data.clearingCourse),
			);
	}

	getClearingUniversity(uniSlug: string) {
		return this._apollo
			.query<UniversityClearingCourseResponse>({
				query: GET_CLEARING_UNIVERSITY,
				variables: {
					uniSlug: uniSlug,
				},
			})
			.pipe(
				catchError((error) => {
					if (error.networkError) {
						const code = error.networkError.error?.data?.code;
						if (code === GraphQLErrorCode.CLEARING_UNI_404) {
							this._router.navigateByUrl('/courses/clearing');
						}
					}
					return EMPTY;
				}),
				map(({ data }) => data.clearingUniversity),
			);
	}
}
